//third party
import { useAtom } from "jotai";
import { useEffect, useState, useRef } from "react";
import { Row, Col, Card, Form, Alert } from "react-bootstrap";
import { Navigate, useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { CreditCard, Bank, InfoCircle, ArrowLeft } from "react-bootstrap-icons";

//spotsync
import { authAtom, InviteMonthlyCheckout, MonthlyCheckout, monthlyCheckoutAtom, redirectAtom, RegularMonthlyCheckout } from "./Data/Atoms";
import { Button } from "./Controls/Button";
import { MonthlyParkingDetails } from "./Controls/MonthlyParkingDetails";
import { MonthlyParkingInviteDetails } from "./Controls/MonthlyParkingInviteDetails";
import { EmailForm } from "./CheckoutPageHourly";
import { CheckoutIndicator } from "./Controls/CheckoutIndicator";
import { VehicleCard } from "./Controls/VehicleCard";
import { Content } from "./Controls/Content";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { SubscribeInitResp, SubscribeInviteInitResp } from "./Data/ApiTypes";
import { OptInFeesSelection } from "./Controls/OptInFeesSelector";
import { stripePromise } from "./Data/ApiStripe";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { LocalDate } from "@js-joda/core";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { Hyperlink } from "./Controls/Hyperlink";
import { CardHeader } from "./Controls/CardHeader";
import { TermsModal } from "./Controls/TermsModal";
import { StripeMonthlyCheckoutForm } from "./StripeCheckoutForm";
import { parseSubscribeInitResp } from "./Data/ApiParse";
import { emitSubscribeInitReq } from "./Data/ApiEmit";
import { isFeatureEnabled, FEATURES } from './Data/Features';
import { getSubscriptionErrorMessage, isSubscriptionErrorType } from './Data/Subscription';
import { ErrorModal } from './Controls/ErrorModal';
import { WaitlistNoticeModal } from "./WaitlistNoticeModal";

enum CheckoutStep {
    Initial,
    Account,
    PaymentType,
    Reservation,
    Payment
}

enum PaymentMethod {
    Card = "card",
    ACH = "ach"
}

type CheckoutPageProps = {
    isInvite?: boolean;
}

export function CheckoutPageMonthlyCombined({ isInvite = false }: CheckoutPageProps) {
    const ldCheckout = useLoaderData() as MonthlyCheckout;
    const loc = useLocation();
    const nav = useNavigate();
    const [auth, setAuth] = useAtom(authAtom);
    const [redirect, setRedirect] = useAtom(redirectAtom);
    const [checkoutUnd, _] = useAtom(monthlyCheckoutAtom);
    const [checkout, setCheckout] = useState<MonthlyCheckout>(ldCheckout ?? checkoutUnd!);
    const [step, setStep] = useState(CheckoutStep.Initial);
    const [terms, setTerms] = useState<string>();
    const [email, setEmail] = useState<EmailForm>({ emailAddress: auth.isLoggedIn ? auth.account!.emailAddress : "" });
    const [subInitResp, setSubInitResp] = useState<SubscribeInitResp | SubscribeInviteInitResp>();
    const [paymentVis, setPaymentVis] = useState<"hidden" | "visible">("hidden");
    const [showTerms, setShowTerms] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.Card);
    const [achEnabled, setAchEnabled] = useState(() => isFeatureEnabled(checkout.facility, FEATURES.ACH));
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>("");
    const [showWaitlistModal, setShowWaitlistModal] = useState(false);
    const [waitlistEntryId, setWaitlistEntryId] = useState(0);
    const [selectedOptInFees, setSelectedOptInFees] = useState<number[]>([]);

    const regularMonthlyCheckout = checkout as RegularMonthlyCheckout;
    const inviteMonthlyCheckout = checkout as InviteMonthlyCheckout;

    const isPublicCheckout = !isInvite && !waitlistEntryId;

    // Add refs for each step
    const accountRef = useRef<HTMLDivElement>(null);
    const detailsRef = useRef<HTMLDivElement>(null);
    const paymentTypeRef = useRef<HTMLDivElement>(null);
    const paymentDetailsRef = useRef<HTMLDivElement>(null);

    // Function to handle step changes with smooth scrolling
    const handleStepChange = (newStep: CheckoutStep) => {
        setStep(newStep);
        
        // Wait for the next render cycle to ensure the new step is visible
        setTimeout(() => {
            let targetRef;
            switch (newStep) {
                case CheckoutStep.Account:
                    targetRef = accountRef;
                    break;
                case CheckoutStep.Reservation:
                    targetRef = detailsRef;
                    break;
                case CheckoutStep.PaymentType:
                    targetRef = paymentTypeRef;
                    break;
                case CheckoutStep.Payment:
                    targetRef = paymentDetailsRef;
                    break;
            }
            
            if (targetRef?.current) {
                targetRef.current.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 100);
    };

    const handleOptInFeesChange = (selection: OptInFeesSelection) => {
        // Convert selection object to array of selected fee IDs
        const selectedIds = Object.keys(selection)
            .filter(id => selection[parseInt(id)])
            .map(id => parseInt(id));
        setSelectedOptInFees(selectedIds);
    };

    useEffect(() => {
        if (!checkout.isInvite) {
            Api.planTermsGet(checkout.option.planId).then(
                res => processResult(res,
                    val => setTerms(val),
                    err => console.error(`Didn't retrieve terms because: ${err}`)));
        } else {
            Api.planTermsGet(checkout.invite.listedPlan.planId).then(
                res => processResult(res,
                    val => setTerms(val),
                    err => console.error(`Didn't retrieve terms because: ${err}`)));
        }
    }, []);

    useEffect(() => {
        if (auth.isLoggedIn) {
            setEmail({ emailAddress: auth.account!.emailAddress });
            setStep(CheckoutStep.PaymentType);
            return;
        }
        //else
        setRedirect(`${loc.pathname}${loc.search}`);
        nav("/login", {
            state: {
                inviteData: {
                    facility: {
                        name: regularMonthlyCheckout.facility.name,
                        address: {
                            addressLine1: regularMonthlyCheckout.facility.address.addressLine1,
                            addressLine2: regularMonthlyCheckout.facility.address.addressLine2,
                            city: regularMonthlyCheckout.facility.address.city,
                            state: regularMonthlyCheckout.facility.address.state,
                            zipCode: regularMonthlyCheckout.facility.address.zipCode
                        }
                    },
                    price: checkout.isInvite 
                        ? inviteMonthlyCheckout.invite.privatePrice.toJSON().amount
                        : regularMonthlyCheckout.option.price.toJSON().amount
                }
            }
    });
    }, [auth.isLoggedIn]);

    useEffect(() => {
        if (step === CheckoutStep.Payment) {
            if (checkout.isInvite) {        
                const subscribeInitReq = {
                    parkerPlanInviteId: checkout.inviteId,
                    start: checkout.invite.startDate ?? LocalDate.now(),
                    quantity: checkout.quantity,
                    isAch: paymentMethod === PaymentMethod.ACH,
                    selectedOptInFees: selectedOptInFees.length > 0 ? selectedOptInFees : undefined
                };
                Api.subscriptionInviteInit(subscribeInitReq).then(
                    res => processResult(res,
                        val => {
                            setWaitlistEntryId(inviteMonthlyCheckout.waitlistEntryId ?? 0);
                            setSubInitResp(val);
                            setPaymentVis("visible");
                        },
                        err => {
                            console.error(err);
                            const errorMessage = isSubscriptionErrorType(err) 
                                ? getSubscriptionErrorMessage(err)
                                : "An unexpected error occurred. Please try again or contact support.";
                            // Show your error modal here with errorMessage
                            setErrorModalVisible(true);
                            setErrorModalMessage(errorMessage);
                            handleStepChange(CheckoutStep.Reservation);
                        }));
            } else {
                const subscribeInitReq = {
                    facilityId: checkout.facility.facilityId,
                    planEntryId: checkout.option.planEntryId,
                    start: checkout.start,
                    quantity: checkout.quantity,
                    isAch: paymentMethod === PaymentMethod.ACH,
                    couponPriceId: checkout.couponPriceId,
                    selectedOptInFees: selectedOptInFees.length > 0 ? selectedOptInFees : undefined
                };
                Api.subscriptionInit(emitSubscribeInitReq(subscribeInitReq)).then(
                    res => processResult(res,
                        val => {
                            setWaitlistEntryId(val.waitlistEntryId ?? 0);
                            setSubInitResp(parseSubscribeInitResp(val));
                            setPaymentVis("visible");
                        },
                        err => {
                            if (err == 'NoWaitlisted')
                            {
                                setShowWaitlistModal(true);
                                return;
                            }
                            console.error(err);
                            const errorMessage = isSubscriptionErrorType(err) 
                                ? getSubscriptionErrorMessage(err)
                                : "An unexpected error occurred. Please try again or contact support.";
                            // Show your error modal here with errorMessage
                            setErrorModalVisible(true);
                            setErrorModalMessage(errorMessage);
                            handleStepChange(CheckoutStep.Reservation);
                        }));
            }
        }
    }, [step]);

    const options: StripeElementsOptions = {
        clientSecret: subInitResp?.paymentIntentClientSecret,
        customerSessionClientSecret: subInitResp?.customerSessionClientSecret,
        appearance: appearanceOptions,
        fonts: stripeFonts
    };

    if (!checkout) {
        return <Navigate to="/results" />;
    }

    return <Content fluid>
        <Row className="justify-content-center">
            <Col md="6">
                <TermsModal terms={terms ?? "Error!"}
                    show={showTerms}
                    setShow={setShowTerms} />

                <ErrorModal
                    show={errorModalVisible}
                    onHide={() => {setErrorModalVisible(false); nav(-1)}}
                    message={errorModalMessage}
                />

                <Card className="mb-2" ref={accountRef}>
                    <CardHeader>
                        <CheckoutIndicator step={1} success={step > CheckoutStep.Account} /> Account Information
                    </CardHeader>
                    <Card.Body>
                        {email.emailAddress == "" && <>You must <Hyperlink to={`/login`}>log in</Hyperlink> to purchase Monthly parking.</>}
                        {email.emailAddress !== "" && <>Checking out as {email.emailAddress}</>}
                    </Card.Body>
                </Card>

                <Card className="mb-2" ref={paymentTypeRef}>
                    <CardHeader>
                        <CheckoutIndicator step={2} success={step > CheckoutStep.PaymentType} /> Payment Type
                    </CardHeader>
                    {step === CheckoutStep.PaymentType && (
                        <Card.Body>
                            <Form>
                            <Button variant="outline-secondary" size="sm" className="mb-4 px-3" onClick={() => {
                                nav("/");
                                setSubInitResp(undefined);
                            }}><ArrowLeft className="me-1 icon-shift-up" />Back To Search</Button>
                                <div className="d-flex flex-column gap-3">
                                    <Form.Check
                                        type="radio"
                                        id="card-payment"
                                        name="payment-method"
                                        className="payment-method-radio"
                                        checked={paymentMethod === PaymentMethod.Card}
                                        onChange={() => setPaymentMethod(PaymentMethod.Card)}
                                        label={
                                            <div className="d-flex align-items-center gap-3 p-3 border rounded">
                                                <CreditCard size={24} className="text-primary" />
                                                <div>
                                                    <div className="fw-bold">Credit/Debit Card</div>
                                                    <div className="small text-muted">Pay with your Card or Digital Wallet</div>
                                                </div>
                                            </div>
                                        }
                                    />
                                    {achEnabled && (
                                        <Form.Check
                                            type="radio"
                                            id="ach-payment"
                                            name="payment-method"
                                            className="payment-method-radio"
                                            checked={paymentMethod === PaymentMethod.ACH}
                                            onChange={() => setPaymentMethod(PaymentMethod.ACH)}
                                            label={
                                                <div className="d-flex align-items-center gap-3 p-3 border rounded">
                                                    <Bank size={24} className="text-primary" />
                                                    <div>
                                                        <div className="fw-bold">Bank Transfer (ACH)</div>
                                                        <div className="small text-muted">Pay directly from your bank account</div>
                                                    </div>
                                                </div>
                                            }
                                        />
                                    )}
                                </div>
                                <div className="mt-4">
                                    <Button
                                        className="w-100 btn-main"
                                        onClick={() => handleStepChange(CheckoutStep.Reservation)}
                                    >
                                        Continue to Details
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    )}
                </Card>

                <Card className="mb-2" ref={detailsRef}>
                    <CardHeader>
                        <CheckoutIndicator step={3} success={step > CheckoutStep.Reservation} /> Monthly Parking Details
                    </CardHeader>
                    {step > CheckoutStep.PaymentType && <Card.Body>
                        <Button variant="outline-secondary" size="sm" className="mb-2 px-3" onClick={() => {
                            setStep(CheckoutStep.PaymentType);
                            setPaymentVis("hidden");
                            setSubInitResp(undefined);
                        }}><ArrowLeft className="me-1 icon-shift-up" />Back To Payment Type</Button>
                        {checkout.isInvite ? (
                            <MonthlyParkingInviteDetails
                                invite={checkout.invite}
                                quantity={checkout.quantity}
                                setQuantity={qty => setCheckout({ ...checkout, quantity: qty })}
                                isAch = {paymentMethod === PaymentMethod.ACH}
                                onOptInFeesChange={handleOptInFeesChange}
                            />
                        ) : (
                            <>
                                <MonthlyParkingDetails
                                    disabled={step !== CheckoutStep.Reservation}
                                    facilityId= {checkout.facility.facilityId}
                                    listedPlans={checkout.facility.listedPlans}
                                    start={checkout.start}
                                    selOpt={checkout.option}
                                    setSelOpt={(plan) => setCheckout({ ...checkout, option: plan })}
                                    quantity={checkout.quantity}
                                    setQuantity={qty => setCheckout({ ...checkout, quantity: qty })}
                                    isCheckoutPage={true}
                                    isAch = {paymentMethod === PaymentMethod.ACH}
                                    isPublicCheckout = {isPublicCheckout}
                                    onCouponPriceChange={(priceId) => 
                                        setCheckout({ ...checkout, couponPriceId: priceId })
                                    }
                                    onOptInFeesChange={handleOptInFeesChange}
                                />
                                <div className="mt-2">
                                    {checkout.vehicles.map((v, i) => <VehicleCard
                                        key={i}
                                        vehicle={v}
                                        deleteVehicle={() => 0}
                                        vehicles={checkout.vehicles}
                                        disabled={step !== CheckoutStep.Reservation} />)}
                                </div>
                            </>
                        )}
                        <div className="my-4 text-center">
                            By clicking "Continue", you agree to our <Hyperlink onClick={() => setShowTerms(true)}>Terms and Conditions</Hyperlink>
                        </div>
                        {step === CheckoutStep.Reservation && <div className="mt-2">
                            <Button className="w-100 mt-2 btn-main" onClick={() => handleStepChange(CheckoutStep.Payment)}>Continue to Payment</Button>
                        </div>}
                    </Card.Body>}
                </Card>

                <Card className="mb-2" ref={paymentDetailsRef}>
                    <CardHeader>
                        <CheckoutIndicator step={4} success={step > CheckoutStep.Payment} /> Payment Details
                    </CardHeader>
                    {step === CheckoutStep.Payment && <Card.Body>
                        <Button variant="outline-secondary" size="sm" className="mb-3 px-3" onClick={() => {
                            setStep(CheckoutStep.Reservation);
                            setPaymentVis("hidden");
                            setSubInitResp(undefined);
                        }}><ArrowLeft className="me-1 icon-shift-up" />Back To Details</Button>
                        {paymentMethod === PaymentMethod.ACH &&
                            <Alert variant="info" className="mt-2" style={{ backgroundColor: 'var(--color-light)' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <InfoCircle size={50} />
                                    <span>By providing your bank account information, you permit {checkout.facility.name} to debit your bank account on the renewal date of this subscription.</span>
                                </div>
                            </Alert>
                        }

                        <div style={{ visibility: paymentVis }}>
                            {subInitResp && <Elements stripe={stripePromise} options={options}>
                                <StripeMonthlyCheckoutForm subscribeInitResp={subInitResp} waitlistEntryId={waitlistEntryId}/>
                            </Elements>}
                        </div>
                    </Card.Body>}
                        <WaitlistNoticeModal
                        show={showWaitlistModal}
                        requestPayload={{
                            parkerId: auth.account?.parkerId,
                            facilityId: ldCheckout.facility.facilityId,
                            planEntryId: regularMonthlyCheckout.option?.planEntryId ?? inviteMonthlyCheckout.invite.listedPlan.planEntryId,
                            spacesRequested: ldCheckout.quantity,
                            isAddingToExistingParker: false
                        }}
                        onCancel={() => {setShowWaitlistModal(false); nav(-1);}}
                        />
                </Card>
            </Col>
        </Row>
    </Content>;
} 